import { type ReactNode, type RefObject, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  type StillRequest,
  type StillSize,
  type StillTarget,
  useStill,
  useStillEstimate,
} from "../api/stills";
import type { Dressing } from "../api/types";
import { rowMessageFor } from "../copy/errors";
import { ErrorLine } from "../ui/ErrorLine";
import { formatApprox } from "../ui/format";
import { useNow } from "../ui/useNow";
import { Button, ImageLightbox } from "../ui";
import type { ViewerHandle } from "../viewer/Viewer";

export const TRUE_RENDER_TITLE =
  "A fully lit picture, as the ad's renderer would draw it";
export const UNSAVED_IMAGE = "Save this look first to render your image.";

const LINE = "m-0 text-sm text-slate-700";
/** The service takes a zoom of 0.5 to 3; the 3D view zooms 0.2 to 8. */
const ZOOM = [0.5, 3] as const;

/** The picture is 768 px in a window narrower than 768 px, else 1024 px. */
function pictureSize(): StillSize {
  return typeof window !== "undefined" && window.innerWidth < 768 ? 768 : 1024;
}

/** "about a minute" from 45 to 90 seconds, else the approximate time. */
function timeWords(seconds: number): string {
  return seconds >= 45 && seconds <= 90
    ? "about a minute"
    : formatApprox(seconds);
}

export interface TrueRenderButtonProps {
  target: StillTarget;
  /** The page's choices. An image that is not saved yet (an object, not an
   *  id) cannot be rendered: the button says so and posts nothing. */
  dressing: Pick<Dressing, "materials" | "skins" | "colors_hex">;
  poses: Record<string, number>;
  /** The 3D view, for the angle on screen. Without one (no WebGL) the
   *  picture is taken from the angle the view opens with. */
  viewerRef?: RefObject<ViewerHandle> | null;
  /** When set, everything after the click (the progress with Stop, the
   *  result or failure sentence, the thumbnail) is drawn into this element
   *  instead of beside the button: the design page's status line. */
  statusTarget?: HTMLElement | null;
  /** The button as an item of a ⋯ menu (the design page on a phone): one
   *  `menuitem` with the price as its second line, disabled until the price
   *  has loaded. `onPressed` closes the menu. */
  menu?: { onPressed(): void };
}

/** **True render**: a fully lit picture of what the page shows, from the
 *  angle on screen, with its price shown before the click
 *  ([design-studio §12.7], [ux §8.20]). */
export function TrueRenderButton({
  target,
  dressing,
  poses,
  viewerRef,
  statusTarget,
  menu,
}: TrueRenderButtonProps) {
  const [size] = useState<StillSize>(pictureSize);
  const estimate = useStillEstimate(size);
  const { start, still, cancel, error, pending } = useStill();
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const now = useNow(1_000, pending);
  // The latest picture of this session, kept beside the button.
  const [picture, setPicture] = useState<{ url: string } | null>(null);
  const [open, setOpen] = useState(false);

  const doneUrl = still?.state === "done" ? still.url : null;
  useEffect(() => {
    if (!doneUrl) return;
    setPicture({ url: doneUrl });
    setOpen(true);
  }, [doneUrl]);

  const unsaved = Object.values(dressing.skins).some(
    (value) => typeof value !== "string",
  );
  const priced = estimate.data !== undefined;
  const disabled = !priced || unsaved || pending;

  const onClick = () => {
    if (disabled) return;
    const view = viewerRef?.current?.view();
    const body: StillRequest = {
      target,
      dressing: {
        materials: dressing.materials,
        skins: dressing.skins as Record<string, string>,
        colors_hex: dressing.colors_hex,
      },
      poses,
      size,
    };
    if (view) {
      body.camera = {
        azimuth_deg: view.azimuth_deg,
        elevation_deg: view.elevation_deg,
        distance_factor: Math.min(
          Math.max(view.distance_factor, ZOOM[0]),
          ZOOM[1],
        ),
      };
    }
    setStartedAt(Date.now());
    start(body);
  };

  // a CPU still is free (§15.1): only the wait is said
  const priceText = estimate.data
    ? `Free; ${timeWords(estimate.data.seconds)}.`
    : null;
  let price: ReactNode = null;
  if (estimate.isError) {
    price = <ErrorLine error={estimate.error} context="still" />;
  } else if (unsaved) {
    price = <p className={LINE}>{UNSAVED_IMAGE}</p>;
  } else if (priceText) {
    price = (
      <p className={LINE} data-testid="still-price">
        {priceText}
      </p>
    );
  }

  // What the last true render came to, in words.
  let outcome: string | null = null;
  if (!pending && still) {
    if (still.state === "failed") {
      outcome = rowMessageFor(still.error_code, { size: still.size });
    } else if (still.state === "cancelled") {
      outcome = "Stopped.";
    }
  }

  const elapsed =
    startedAt === null ? 0 : Math.max(0, Math.round((now - startedAt) / 1000));
  const status = (
    <>
      {pending ? (
        <p className={`${LINE} flex flex-wrap items-center gap-2`}>
          <span role="status">Rendering… {elapsed} s</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="min-h-[44px] sm:min-h-0"
            onClick={cancel}
          >
            Stop
          </Button>
        </p>
      ) : null}
      {!pending && error ? <ErrorLine error={error} context="still" /> : null}
      {outcome ? (
        <p role="alert" className={LINE}>
          {outcome}
        </p>
      ) : null}
      {picture ? (
        <button
          type="button"
          className="self-start rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
          onClick={() => setOpen(true)}
        >
          <img
            src={picture.url}
            alt="The latest true render"
            className="block h-16 w-16 rounded-md bg-slate-100 object-cover"
          />
        </button>
      ) : null}
    </>
  );

  return (
    <div className="flex flex-col gap-2" data-testid="true-render">
      {menu ? (
        <>
          <button
            type="button"
            role="menuitem"
            className="min-h-[44px] rounded px-2 py-1.5 text-left hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
            title={unsaved ? UNSAVED_IMAGE : TRUE_RENDER_TITLE}
            aria-busy={pending || estimate.isPending ? true : undefined}
            disabled={disabled}
            onClick={() => {
              onClick();
              menu.onPressed();
            }}
          >
            <span className="block font-semibold">
              {pending ? "Rendering…" : "True render"}
            </span>
            {unsaved ? (
              <span className="block text-xs text-slate-600">
                {UNSAVED_IMAGE}
              </span>
            ) : priceText ? (
              <span
                className="block text-xs text-slate-600"
                data-testid="still-price"
              >
                {priceText}
              </span>
            ) : null}
          </button>
          {estimate.isError ? (
            <ErrorLine error={estimate.error} context="still" />
          ) : null}
        </>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            className="min-h-[44px] disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
            title={unsaved ? UNSAVED_IMAGE : TRUE_RENDER_TITLE}
            aria-busy={pending || estimate.isPending ? true : undefined}
            disabled={disabled}
            onClick={onClick}
          >
            {pending ? "Rendering…" : "True render"}
          </Button>
          {price}
        </div>
      )}
      {statusTarget ? createPortal(status, statusTarget) : status}
      {open && picture ? (
        <ImageLightbox
          src={picture.url}
          alt="True render"
          label="True render"
          fit="contain"
          onClose={() => setOpen(false)}
        >
          <a href={picture.url} download="true-render.png">
            Download
          </a>
          <span>Kept for 7 days; download it to keep it.</span>
        </ImageLightbox>
      ) : null}
    </div>
  );
}
